/**
 * Family → drawer registry (replaces the giant switch).
 * Add a new family drawer here after implementing draw* in the domain file.
 */
import type { App } from '../../../App';
import type { Group } from '../../../shapes/Group';
import type { PipelineGlyphFamily } from '../types';
import { ENV_MARK, SYSTEM_MARK } from '../familyMap';
import { resolvePipelineSymbolKind } from '../resolve';
import { PIPELINE_FAMILY } from '../familyMap';

import { drawActivity, drawAnnotation, drawArrow, drawBusinessProcess, drawBusinessRule, drawCapsuleEnd, drawCapsuleStart, drawChevronIn, drawChevronOut, drawCircleEvent, drawComment, drawDecisionPoint, drawDependency, drawDestination, drawDiamond, drawEngineeringProcess, drawError, drawEscalation, drawException, drawFlowPath, drawFork, drawGatewayCircle, drawGatewayMerge, drawGatewayPlus, drawGatewayX, drawHandover, drawInfo, drawJoin, drawLinkChain, drawLoopMark, drawManualTask, drawManufacturingProcess, drawMessage, drawOperation, drawPipelineFlow, drawProcess, drawRetry, drawScriptTask, drawServiceTask, drawSignal, drawSoftwareProcess, drawSource, drawSplit, drawSubprocess, drawSynchronization, drawTaskBox, drawTimer, drawTrigger, drawUserTask, drawValueStream, drawWait, drawWarning, drawWorkflow } from './flow';
import { drawAnalytics, drawApi, drawArchiveStorage, drawBackup, drawBroker, drawBuffer, drawChart, drawChat, drawCylinder, drawDataObject, drawDataset, drawDocument, drawEmail, drawEnvelope, drawEventBus, drawFile, drawFolder, drawGlobe, drawKpi, drawLog, drawMessageQueue, drawMetric, drawNotification, drawPhone, drawReport, drawStack, drawWebhook } from './data';
import { drawAi, drawAppContainer, drawArtifact, drawBranch, drawBuild, drawCalendar, drawCheckpoint, drawCicd, drawCloud, drawCompile, drawDeploy, drawEnv, drawGit, drawHexService, drawK8s, drawLane, drawLibrary, drawMergeRequest, drawMicroservice, drawModule, drawMonitoring, drawPackage, drawPin, drawPool, drawPrototype, drawRelease, drawRollback, drawServer, drawSimulation, drawSystem, drawTag, drawVersion, drawVm, drawWorkstation } from './software';
import { drawAcceptanceTest, drawAgv, drawAssembly, drawBarcode, drawBlockDiagram, drawBom, drawCadModel, drawCalibration, drawCamera, drawCargoContainer, drawCnc, drawConveyor, drawCrane, drawCrate, drawCustomerDelivery, drawFinishedGoods, drawForklift, drawFunctionalTest, drawIntegrationTest, drawInventory, drawIot, drawLoadingDock, drawMachine, drawManufacturingCell, drawPackaging, drawPallet, drawProductionLine, drawQualityAssurance, drawQualityControl, drawRawMaterial, drawReliabilityTest, drawRepair, drawRfid, drawRobot, drawScanner, drawSchematicDoc, drawScrap, drawSensor, drawTestBench, drawTruck, drawUnitTest, drawWarehouse, drawWaste, drawWip, drawWrench } from './manufacturing';
import { drawApproval, drawAuthorization, drawBug, drawCertification, drawChange, drawCheck, drawCoin, drawCompliance, drawCustomer, drawDollar, drawEncryption, drawEngineer, drawEuro, drawFirewall, drawFlag, drawGoal, drawInsect, drawInspection, drawIssue, drawManager, drawMilestone, drawObjective, drawOperator, drawPeople, drawPerson, drawPhase, drawReview, drawRisk, drawRupee, drawSecretVault, drawShield, drawSignOff, drawSprint, drawTechnician, drawTimeline, drawValidation, drawVendor, drawVerification, drawVirus } from './peopleGovernance';
import { drawBuilding, drawFactory, drawHome, drawLift, drawMarket, drawPark, drawParking, drawSchool, drawShop, drawStadium, drawStaircase, drawTemple, drawWindow } from './facilities';
import { drawAutoVehicle, drawBike, drawBoat, drawBus, drawCar, drawCycle, drawFlight, drawHelicopter, drawRickshaw, drawRoad, drawShip, drawVan } from './transport';
import { drawBluetooth, drawEarth, drawForest, drawGps, drawHillStation, drawLaptop, drawLight, drawMap, drawPhoneTower, drawPlant, drawSolarPanel, drawStarMark, drawSun, drawUmbrella, drawWifi } from './natureDevices';

export type GlyphDrawer = (app: App, g: Group) => void;

export const PIPELINE_DRAWERS: Partial<Record<PipelineGlyphFamily, GlyphDrawer>> = {
  capsuleStart: drawCapsuleStart,
  capsuleEnd: drawCapsuleEnd,
  process: drawProcess,
  subprocess: drawSubprocess,
  userTask: drawUserTask,
  serviceTask: drawServiceTask,
  manualTask: drawManualTask,
  scriptTask: drawScriptTask,
  businessRule: drawBusinessRule,
  diamond: drawDiamond,
  gatewayPlus: drawGatewayPlus,
  gatewayX: drawGatewayX,
  gatewayCircle: drawGatewayCircle,
  gatewayMerge: drawGatewayMerge,
  circleEvent: drawCircleEvent,
  signal: drawSignal,
  escalation: drawEscalation,
  timer: drawTimer,
  message: drawMessage,
  error: drawError,
  exception: drawException,
  trigger: drawTrigger,
  warning: drawWarning,
  info: drawInfo,
  comment: drawComment,
  chevronIn: drawChevronIn,
  chevronOut: drawChevronOut,
  arrow: drawArrow,
  pipelineFlow: drawPipelineFlow,
  flowPath: drawFlowPath,
  valueStream: drawValueStream,
  stack: drawStack,
  document: drawDocument,
  folder: drawFolder,
  cylinder: drawCylinder,
  chart: drawChart,
  phone: drawPhone,
  api: drawApi,
  globe: drawGlobe,
  git: drawGit,
  build: drawBuild,
  compile: drawCompile,
  deploy: drawDeploy,
  rollback: drawRollback,
  cicd: drawCicd,
  shield: drawShield,
  person: drawPerson,
  people: drawPeople,
  check: drawCheck,
  flag: drawFlag,
  coin: drawCoin,
  change: drawChange,
  machine: drawMachine,
  robot: drawRobot,
  conveyor: drawConveyor,
  warehouse: drawWarehouse,
  inventory: drawInventory,
  crate: drawCrate,
  wrench: drawWrench,
  truck: drawTruck,
  forklift: drawForklift,
  hexService: drawHexService,
  sensor: drawSensor,
  cloud: drawCloud,
  server: drawServer,
  ai: drawAi,
  tag: drawTag,
  lane: drawLane,
  pin: drawPin,
  loop: drawLoopMark,
  retry: drawRetry,
  cnc: drawCnc,
  productionLine: drawProductionLine,
  assembly: drawAssembly,
  crane: drawCrane,
  pallet: drawPallet,
  cargoContainer: drawCargoContainer,
  packaging: drawPackaging,
  rawMaterial: drawRawMaterial,
  wip: drawWip,
  finishedGoods: drawFinishedGoods,
  scrap: drawScrap,
  appContainer: drawAppContainer,
  camera: drawCamera,
  barcode: drawBarcode,
  rfid: drawRfid,
  iot: drawIot,
  calendar: drawCalendar,
  chat: drawChat,
  k8s: drawK8s,
  vm: drawVm,
  workstation: drawWorkstation,
  loadingDock: drawLoadingDock,
  waste: drawWaste,
  manufacturingCell: drawManufacturingCell,
  backup: drawBackup,
  archiveStorage: drawArchiveStorage,
  customerDelivery: drawCustomerDelivery,
  agv: drawAgv,
  activity: drawActivity,
  operation: drawOperation,
  taskBox: drawTaskBox,
  workflow: drawWorkflow,
  businessProcess: drawBusinessProcess,
  manufacturingProcess: drawManufacturingProcess,
  engineeringProcess: drawEngineeringProcess,
  softwareProcess: drawSoftwareProcess,
  wait: drawWait,
  linkChain: drawLinkChain,
  dependency: drawDependency,
  annotation: drawAnnotation,
  buffer: drawBuffer,
  fork: drawFork,
  join: drawJoin,
  split: drawSplit,
  module: drawModule,
  file: drawFile,
  dataObject: drawDataObject,
  report: drawReport,
  log: drawLog,
  webhook: drawWebhook,
  email: drawEmail,
  notification: drawNotification,
  microservice: drawMicroservice,
  branch: drawBranch,
  mergeRequest: drawMergeRequest,
  prototype: drawPrototype,
  simulation: drawSimulation,
  kpi: drawKpi,
  metric: drawMetric,
  analytics: drawAnalytics,
  secretVault: drawSecretVault,
  firewall: drawFirewall,
  encryption: drawEncryption,
  authorization: drawAuthorization,
  review: drawReview,
  inspection: drawInspection,
  approval: drawApproval,
  validation: drawValidation,
  handover: drawHandover,
  milestone: drawMilestone,
  phase: drawPhase,
  sprint: drawSprint,
  goal: drawGoal,
  objective: drawObjective,
  cadModel: drawCadModel,
  schematicDoc: drawSchematicDoc,
  blockDiagram: drawBlockDiagram,
  bom: drawBom,
  scanner: drawScanner,
  eventBus: drawEventBus,
  broker: drawBroker,
  messageQueue: drawMessageQueue,
  dataset: drawDataset,
  library: drawLibrary,
  package: drawPackage,
  artifact: drawArtifact,
  release: drawRelease,
  monitoring: drawMonitoring,
  timeline: drawTimeline,
  issue: drawIssue,
  risk: drawRisk,
  unitTest: drawUnitTest,
  integrationTest: drawIntegrationTest,
  functionalTest: drawFunctionalTest,
  acceptanceTest: drawAcceptanceTest,
  reliabilityTest: drawReliabilityTest,
  qualityControl: drawQualityControl,
  qualityAssurance: drawQualityAssurance,
  testBench: drawTestBench,
  calibration: drawCalibration,
  repair: drawRepair,
  customer: drawCustomer,
  engineer: drawEngineer,
  manager: drawManager,
  operator: drawOperator,
  technician: drawTechnician,
  vendor: drawVendor,
  pool: drawPool,
  decisionPoint: drawDecisionPoint,
  checkpoint: drawCheckpoint,
  synchronization: drawSynchronization,
  source: drawSource,
  destination: drawDestination,
  verification: drawVerification,
  signOff: drawSignOff,
  certification: drawCertification,
  version: drawVersion,
  factory: drawFactory,
  building: drawBuilding,
  home: drawHome,
  laptop: drawLaptop,
  gps: drawGps,
  earth: drawEarth,
  starMark: drawStarMark,
  sun: drawSun,
  window: drawWindow,
  solarPanel: drawSolarPanel,
  plant: drawPlant,
  forest: drawForest,
  hillStation: drawHillStation,
  map: drawMap,
  umbrella: drawUmbrella,
  car: drawCar,
  van: drawVan,
  bike: drawBike,
  cycle: drawCycle,
  rickshaw: drawRickshaw,
  autoVehicle: drawAutoVehicle,
  bus: drawBus,
  flight: drawFlight,
  helicopter: drawHelicopter,
  ship: drawShip,
  boat: drawBoat,
  road: drawRoad,
  staircase: drawStaircase,
  lift: drawLift,
  park: drawPark,
  parking: drawParking,
  school: drawSchool,
  stadium: drawStadium,
  shop: drawShop,
  market: drawMarket,
  dollar: drawDollar,
  euro: drawEuro,
  rupee: drawRupee,
  envelope: drawEnvelope,
  virus: drawVirus,
  bug: drawBug,
  insect: drawInsect,
  bluetooth: drawBluetooth,
  phoneTower: drawPhoneTower,
  wifi: drawWifi,
  light: drawLight,
  temple: drawTemple,
  compliance: drawCompliance,
};

/** Paint a pipeline glyph into group `g` (local 48×48 coords). */
export function drawPipelineGlyph(app: App, g: Group, kind: string): void {
  const k = resolvePipelineSymbolKind(kind);
  const family = PIPELINE_FAMILY[k] ?? 'process';

  if (family === 'env') {
    drawEnv(app, g, ENV_MARK[k] ?? 'ENV');
    return;
  }
  if (family === 'system') {
    drawSystem(app, g, SYSTEM_MARK[k] ?? 'SYS');
    return;
  }

  const drawer = PIPELINE_DRAWERS[family] ?? drawProcess;
  drawer(app, g);
}
